import {
  AiRmrInBodyMeasurement,
  AiRmrInBodyMetric,
  AiRmrInBodySummary,
} from "./aiRmrInBodySummaryTypes";
import {
  parseAiSummaryNutrition,
  parseAiSummaryRecommendations,
  parseAiSummaryTrainingPlan,
} from "./aiSummaryResponse";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const getCleanString = (value: unknown): string =>
  getString(value).replace(/^:\s*/, "");

const getFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
  }

  return undefined;
};

const parseJsonString = (value: string): unknown => {
  const normalized = value
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "");

  try {
    return JSON.parse(normalized);
  } catch (error) {
    return value;
  }
};

const isRmrInBodyPayload = (value: UnknownRecord): boolean =>
  "body_composition" in value ||
  "connections_title" in value ||
  "inbody_measurements_title" in value ||
  ("hero" in value && "overview_metrics" in value);

const unwrapResponse = (payload: unknown): unknown => {
  let current =
    typeof payload === "string" ? parseJsonString(payload) : payload;

  for (let depth = 0; depth < 5; depth += 1) {
    if (!isRecord(current) || isRmrInBodyPayload(current)) {
      return current;
    }

    const nested = current.data ?? current.response ?? current.result;
    if (nested === undefined || nested === current) {
      return current;
    }

    current = typeof nested === "string" ? parseJsonString(nested) : nested;
  }

  return current;
};

const parseOverviewMetrics = (value: unknown): AiRmrInBodyMetric[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<AiRmrInBodyMetric[]>((metrics, item) => {
    if (!isRecord(item)) {
      return metrics;
    }

    const label = getString(item.label);
    const metricValue = getFiniteNumber(item.value);
    if (!label || metricValue === undefined) {
      return metrics;
    }

    const scoreCategory = getFiniteNumber(item.score_category);
    metrics.push({
      id: getString(item.id) || label,
      label,
      value: metricValue,
      unit: getString(item.unit),
      ...(scoreCategory !== undefined ? { scoreCategory } : {}),
      scoreCategoryLabel: getString(item.score_category_label),
    });
    return metrics;
  }, []);
};

const parseBodyComposition = (value: unknown): AiRmrInBodyMeasurement[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<AiRmrInBodyMeasurement[]>((measurements, item) => {
    if (!isRecord(item)) {
      return measurements;
    }

    const label = getString(item.label);
    const measurementValue = getFiniteNumber(item.value);
    if (!label || measurementValue === undefined) {
      return measurements;
    }

    measurements.push({
      label,
      value: measurementValue,
      unit: getString(item.unit),
    });
    return measurements;
  }, []);
};

export const parseAiRmrInBodySummaryResponse = (
  payload: unknown
): AiRmrInBodySummary => {
  const unwrapped = unwrapResponse(payload);
  const root = isRecord(unwrapped) ? unwrapped : {};
  const hero = isRecord(root.hero) ? root.hero : {};
  const rmr = isRecord(hero.rmr) ? hero.rmr : {};
  const patient = isRecord(root.patient) ? root.patient : {};
  const recommendations = isRecord(root.recommendations)
    ? root.recommendations
    : {};
  const services = isRecord(root.services) ? root.services : {};
  const rootNutrition = parseAiSummaryNutrition(root.nutrition);
  const recommendationNutrition = parseAiSummaryNutrition(
    recommendations.nutrition
  );
  const rmrValue = getFiniteNumber(rmr.value);
  const fatPercent = getFiniteNumber(hero.fat_pct);
  const carbPercent = getFiniteNumber(hero.carb_pct);

  return {
    hero: {
      ...(rmrValue !== undefined
        ? { rmr: { value: rmrValue, unit: getString(rmr.unit) } }
        : {}),
      ...(fatPercent !== undefined ? { fatPercent } : {}),
      ...(carbPercent !== undefined ? { carbPercent } : {}),
    },
    patient: {
      name: getCleanString(patient.name),
      companyName: getCleanString(patient.company_name),
      rmrTestDate: getCleanString(patient.rmr_test_date),
      inBodyTestDate: getCleanString(patient.inbody_test_date),
    },
    reportType: getString(root.report_type),
    connectionsTitle: getString(root.connections_title),
    pnoeMeasurementsTitle: getString(root.pnoe_measurements_title),
    inBodyMeasurementsTitle: getString(root.inbody_measurements_title),
    rmrMuscleDescription: getString(root.rmr_muscle_description),
    fatBurnBodyFatDescription: getString(root.fat_burn_body_fat_description),
    flexibilityPhaseAngleDescription: getString(
      root.flexibility_phase_angle_description
    ),
    overviewMetrics: parseOverviewMetrics(root.overview_metrics),
    bodyComposition: parseBodyComposition(root.body_composition),
    trainingPlan: parseAiSummaryTrainingPlan(
      root.workout ?? recommendations.exercise
    ),
    nutrition: {
      workoutDays:
        rootNutrition.workoutDays ?? recommendationNutrition.workoutDays,
      nonWorkoutDays:
        rootNutrition.nonWorkoutDays ?? recommendationNutrition.nonWorkoutDays,
    },
    recommendations: parseAiSummaryRecommendations(recommendations, services),
    disclaimer: getString(root.disclaimer),
  };
};
